import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import Button from '../components/Button';
import LoadingIndicator from '../components/LoadingIndicator';
import Modal, { ModalTitle } from '../components/Modal';
import { gql } from '../graphql/generated/gql';
import { useAddTopicToPost } from '../post/sharing/postPermissionOperations';
import parseGraphQLError from '../utils/error/parseGraphQLError';
import useScript from '../utils/network/useScript';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';
import * as styles from './GoogleDrivePicker.module.scss';

const GOOGLE_API_URL = 'https://apis.google.com/js/api.js';

const GOOGLE_IMPORT_MUTATION = gql(/** @complexity 2 */ `
  mutation googleImport($externalId: String!) {
    importExternal(platform: "google-drive", externalId: $externalId) {
      ...postPath_post
    }
  }
`);

interface GoogleDrivePickerProps {
  close: () => void;
  navigationMethod?: 'push' | 'replace';
  topicIds?: string[];
  onBeforeNavigate?: () => Promise<void>;
}

interface GoogleDrivePickerAuthenticatedProps extends GoogleDrivePickerProps {
  accessToken: string;
  publishableKey: string;
  setError: (error: string) => void;
}

const GoogleDrivePickerAuthenticated = ({
  close,
  navigationMethod = 'push',
  topicIds,
  accessToken,
  publishableKey,
  setError,
  onBeforeNavigate,
}: GoogleDrivePickerAuthenticatedProps) => {
  const navigate = useNavigate();

  const [importExternal, { loading }] = useMutation(GOOGLE_IMPORT_MUTATION);

  const addTopic = useAddTopicToPost();

  const onResultRef = useRef<(result: google.picker.ResponseObject) => void>(
    () => {},
  );

  useEffect(() => {
    const handleImport = async (externalId: string) => {
      try {
        const response = await importExternal({ variables: { externalId } });

        const post = response.data?.importExternal;
        if (post) {
          close();
          if (Array.isArray(topicIds)) {
            await Promise.all(
              topicIds.map((topicId) => addTopic(post.id, topicId)),
            );
          }
          if (onBeforeNavigate) {
            await onBeforeNavigate();
          }
          navigate(postPath(post), { replace: navigationMethod === 'replace' });
        }
      } catch (e) {
        setError(
          parseGraphQLError(e).code === 'INVALID_FORMAT'
            ? 'The file format is not supported.'
            : 'We are sorry, but the import failed for unknown reason.',
        );
      }
    };
    onResultRef.current = (result: google.picker.ResponseObject) => {
      switch (result.action) {
        case google.picker.Action.CANCEL: {
          close();
          break;
        }
        case google.picker.Action.PICKED: {
          const externalId = result.docs[0]?.id;
          if (!externalId) {
            setError('Document import failed.');
            break;
          }
          handleImport(externalId);
          break;
        }
        default: {
          break;
        }
      }
    };
  }, [
    addTopic,
    close,
    importExternal,
    navigationMethod,
    navigate,
    setError,
    topicIds,
    onBeforeNavigate,
  ]);

  useEffect(() => {
    let docPicker: google.picker.Picker | undefined;
    window.gapi?.load('picker', () => {
      docPicker = new google.picker.PickerBuilder()
        .setDeveloperKey(publishableKey)
        .setOAuthToken(accessToken)
        .addView(new google.picker.DocsView(google.picker.ViewId.DOCUMENTS))
        .setCallback(onResultRef.current)
        .build();
      docPicker.setVisible(true);
    });

    return () => {
      docPicker?.dispose();
    };
  }, [accessToken, publishableKey]);

  if (loading) return <LoadingIndicator delay={0} card />;

  return null;
};

const GOOGLE_DRIVE_PICKER_QUERY = gql(/** @complexity 3 */ `
  query GoogleDrivePicker($redirectPath: String!) {
    currentSession {
      # eslint-disable-next-line @graphql-eslint/require-selections
      user {
        googleDrivePickerAccessToken(redirectPath: $redirectPath) {
          authorizationUrl
          publishableKey
          token
        }
      }
    }
  }
`);

const getRedirectPath = (topicIds: string[] = []) =>
  `?${new URLSearchParams([
    ['importFrom', 'google'],
    ...topicIds.map((topicId) => ['topicIds', topicId]),
  ])}`;

const GoogleDrivePicker = (props: GoogleDrivePickerProps) => {
  const { close } = props;
  const [error, setError] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(GOOGLE_DRIVE_PICKER_QUERY, {
    variables: { redirectPath: getRedirectPath(props.topicIds) },
  });
  const googleDrivePickerAccessToken =
    data?.currentSession.user.googleDrivePickerAccessToken;

  const { loaded: isScriptLoaded } = useScript(GOOGLE_API_URL);

  if (loading || !isScriptLoaded) {
    return <LoadingIndicator delay={0} card />;
  }

  if (!googleDrivePickerAccessToken) {
    return (
      <Modal noPadding width={370}>
        <div className={styles.modal}>
          <ModalTitle small noMargin>
            Problem loading
          </ModalTitle>
          <div className={styles.content}>
            There is some issue loading Google Drive Picker. Please try again.
          </div>
          <div className={styles.buttons}>
            <Button variant="secondary" onClick={close}>
              Later
            </Button>
            <Button variant="primary" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
  const { token, publishableKey, authorizationUrl } =
    googleDrivePickerAccessToken;

  if (!token) {
    return (
      <Modal noPadding width={370}>
        <div className={styles.modal}>
          <ModalTitle small noMargin>
            Import from Google
          </ModalTitle>
          <div className={styles.content}>
            To continue please sign in with Google account to import your files.
          </div>
          <div className={styles.buttons}>
            <Button variant="secondary" onClick={close}>
              Close
            </Button>
            <Button variant="primary" to={authorizationUrl}>
              Sign in with Google
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal noPadding width={370}>
        <div className={styles.modal}>
          <ModalTitle small noMargin>
            Import error
          </ModalTitle>
          <div className={styles.content}>{error}</div>
          <div className={styles.buttons}>
            <Button variant="secondary" onClick={close}>
              Close
            </Button>
            <Button variant="primary" onClick={() => setError(null)}>
              Try again
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <GoogleDrivePickerAuthenticated
      {...props}
      accessToken={token}
      publishableKey={publishableKey}
      setError={setError}
    />
  );
};

export default GoogleDrivePicker;
